/**
 * SyncErrorBanner.tsx
 *
 * Sync-failure-with-panel-preservation banner. Renders above the panel grid
 * when the most recent `sync:dashboard:complete` event reported one or more
 * domains whose backing credential probed as `unavailable` (the BE skipped
 * those domains and preserved their cached `dashboard_panel_data` rows
 * bit-identically — see TDD §0.3).
 *
 * **Architectural contract (CEO-locked 2026-05-26):**
 *   - Dashboard LOAD: never checks credentials. Panels always render
 *     last-known cached data.
 *   - Sync click is the ONLY credential failure surface.
 *   - On `domains_failed` non-empty response: render this banner. Panels for
 *     failed domains are NOT mutated — they retain their pre-sync cached
 *     data. The ONLY per-panel visible marker is the "(cached)" suffix on
 *     the panel's date meta (rendered by `PanelStaleNote`).
 *
 * **References:**
 *   - Brand guide §37f (Sync Error Banner — Post-Sync Failure with Panel
 *     Preservation).
 *   - TDD §13.2 (FE visual contract — LOCKED 2026-05-26).
 *   - v2 mockup: designs/credential-aware-dashboard-availability/v2/
 *     2026-05-26-sync-error-banner-v2.html.
 *
 * **Dismissibility (§37f):**
 *   Banner is dismissible via the X button. Residual signals
 *   (sync-status dot color, Sync button outline variant, "(cached)" suffix
 *   on panel date meta) persist after dismiss. `syncErrorDismissed` resets
 *   to `false` on the next `startDomainFetch` so a subsequent failed sync
 *   re-shows the banner.
 *
 * **Copy rules (FE-constructed, NOT server-authored):**
 *   - Single failure: "Couldn't refresh: [Domain] data — your credential
 *     needs attention."
 *   - Multiple failures: "Couldn't refresh: [A], [B] data — your credential
 *     needs attention."
 *   - Map `domainId → displayName` using the dashboard domain registry
 *     slice (the same data that drives the picker).
 *
 * **Accessibility (§37f):**
 *   - `role="alert" aria-live="assertive" aria-atomic="true"` — assertive
 *     because the Sync just completed and the user expects feedback.
 *   - Dismiss button: `aria-label="Dismiss sync error notification"`.
 *   - "Manage credentials" link: `aria-label` describes which credential(s).
 */

import React from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, KeyRound, X } from 'lucide-react'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SyncErrorBannerProps {
  /**
   * Human-readable display names of the domains whose sync was SKIPPED on
   * the most recent `sync:dashboard:complete` event (preserved cached data).
   * Composed by the caller from `domainsFailed[].domainId` via the dashboard
   * domain registry's display-name resolver.
   *
   * Non-empty: callers are expected to gate the mount on
   * `domainsFailed.length > 0 && !syncErrorDismissed`.
   */
  failedDomainNames: string[]
  /**
   * Optional human-readable display names of domains that synced
   * successfully in the SAME sync attempt. When non-empty, appended as a
   * partial-success appendix per §37f: "[X] data refreshed successfully."
   */
  partialSuccessNames?: string[]
  /** Called when the user clicks the X dismiss button. */
  onDismiss: () => void
  /**
   * Future extension: when the BE includes a `credentialId` on the
   * `domains_failed[]` shape, the "Manage credentials" link deep-links to
   * `/credentials?highlight={credentialId}`. For v1.x the BE does NOT
   * include `credentialId` on the wire (TDD §13.2) — the link routes to
   * `/credentials` (the credentials list).
   */
  credentialId?: string | null
}

// ─── Component ────────────────────────────────────────────────────────────────

export const SyncErrorBanner: React.FC<SyncErrorBannerProps> = ({
  failedDomainNames,
  partialSuccessNames = [],
  onDismiss,
  credentialId,
}) => {
  const failedNamesText = failedDomainNames.join(', ')

  // Deep-link target per §37f. v1.x BE does not emit credentialId on the
  // wire — link falls through to `/credentials`. When the BE later carries
  // credentialId in `domains_failed[]`, the highlight query string is
  // honoured by the credentials list page (out of scope for Slice 3.2).
  // credentialId is URL-encoded to guard against reserved characters if the
  // BE shape ever changes from a simple UUID slug.
  const manageCredentialsHref = credentialId
    ? `/credentials?highlight=${encodeURIComponent(credentialId)}`
    : '/credentials'

  // ARIA: assertive because the Sync just completed and the user expects
  // feedback (brand guide §37f, contrasted with the polite live region used
  // for non-blocking status). aria-atomic ensures the full banner text is
  // re-announced when failedDomainNames changes between syncs.
  //
  // Pluralise the aria-label noun to match the actual number of failed
  // domains (e.g. "fix the Enrollment data credential" vs "fix the
  // Enrollment, Bunks data credentials"). failedNamesText may be empty
  // if the caller omits the gate — guard with a fallback.
  const credentialNoun =
    failedDomainNames.length === 1 ? 'credential' : 'credentials'
  const ariaLabel =
    failedNamesText.length > 0
      ? `Manage credentials — fix the ${failedNamesText} data ${credentialNoun}`
      : 'Manage credentials'

  return (
    <div
      className="flex items-start gap-3 px-7 py-3 flex-shrink-0 bg-destructive-foreground border-b border-destructive/30 animate-slide-down"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      data-testid="sync-error-banner"
    >
      <AlertCircle
        size={16}
        className="text-destructive flex-shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-red-800 leading-snug">
          Couldn&rsquo;t refresh: {failedNamesText} data &mdash; your credential
          needs attention.
        </p>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <Link
            to={manageCredentialsHref}
            className="text-xs font-semibold text-accent flex items-center gap-1 hover:underline"
            aria-label={ariaLabel}
            data-testid="sync-error-banner-manage-credentials"
          >
            <KeyRound size={11} aria-hidden="true" />
            Manage credentials
          </Link>
          {partialSuccessNames.length > 0 && (
            <>
              <span
                className="text-muted-foreground text-xs"
                aria-hidden="true"
              >
                ·
              </span>
              <span className="text-xs text-red-800/75">
                {partialSuccessNames.join(', ')} data refreshed successfully.
              </span>
            </>
          )}
        </div>
      </div>
      {/* Dismiss — residual signals (sync-status dot, "(cached)" suffix,
          Sync button outline variant) persist after dismiss per §37f. */}
      <button
        type="button"
        className="flex-shrink-0 text-red-800/60 hover:text-red-800 hover:bg-destructive/8 transition-colors w-6 h-6 rounded flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
        aria-label="Dismiss sync error notification"
        onClick={onDismiss}
        data-testid="sync-error-banner-dismiss"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  )
}
